import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, Clock, Video, Mic, MicOff, VideoOff, 
  Phone, PhoneOff, Users, Music, CheckCircle, 
  ChevronLeft, ChevronRight, DollarSign, Headphones,
  Volume2, Settings, Maximize2, Minimize2, Crown,
  Copy, Send, Link as LinkIcon, Mail, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Available time slots - 24 hours for flexibility
const TIME_SLOTS = [
  '12:00 AM', '01:00 AM', '02:00 AM', '03:00 AM', '04:00 AM', '05:00 AM',
  '06:00 AM', '07:00 AM', '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM', 
  '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM',
  '06:00 PM', '07:00 PM', '08:00 PM', '09:00 PM', '10:00 PM', '11:00 PM'
];

const HOURLY_RATE = 100;

// Session types - including test tier for payment testing
const SESSION_TYPES = [
  { id: 'test', name: 'Test Session', price: 2, duration: 1, description: 'For testing payments only' },
  { id: 'standard', name: 'Standard Session', price: 100, duration: 1, description: '1 hour live recording' },
  { id: 'extended', name: 'Extended Session', price: 200, duration: 2, description: '2 hours live recording' },
  { id: 'premium', name: 'Premium Session', price: 300, duration: 3, description: '3 hours live recording' },
];

import AdminFreeAccessBar, { generateStudioRoomCode } from '../components/admin/AdminFreeAccessBar';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const LiveStudioBookingInner = () => {
  const { isAdmin, user } = useAuth();
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;

  // Booking state
  const [step, setStep] = useState(1); // 1: Select Time, 2: Details, 3: Payment, 4: Confirmation, 5: Live Room
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSessionType, setSelectedSessionType] = useState(null);
  const [duration, setDuration] = useState(1); // hours
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Customer info
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  
  // Booking result
  const [booking, setBooking] = useState(null);
  
  // Video room state
  const [inSession, setInSession] = useState(false);
  const [micEnabled, setMicEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Admin invite state
  const [inviteSession, setInviteSession] = useState(null); // { code, join_link }
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteEmailSent, setInviteEmailSent] = useState(false);
  const [joinCodeInput, setJoinCodeInput] = useState('');

  // Video refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  
  // Calendar helpers
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first day
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add all days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };
  
  const isDateAvailable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  };
  
  const isTimeSlotBooked = (time) => {
    if (!selectedDate) return false;
    const dateStr = selectedDate.toISOString().split('T')[0];
    return bookedSlots.some(slot => 
      slot.date === dateStr && slot.time === time
    );
  };
  
  // Fetch booked slots
  useEffect(() => {
    fetchBookedSlots();
  }, [currentMonth]);
  
  const fetchBookedSlots = async () => {
    try {
      const res = await fetch(`${API}/api/live-studio/booked-slots`);
      if (res.ok) {
        const data = await res.json();
        setBookedSlots(data.slots || []);
      }
    } catch (err) {
      console.error('Error fetching slots:', err);
    }
  };
  
  // Check if user has an active booking or returning from payment
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const bookingId = urlParams.get('booking');
    const paymentStatus = urlParams.get('payment');
    const joinSession = urlParams.get('join');
    
    if (bookingId && paymentStatus === 'success') {
      // Returning from successful payment - confirm booking
      confirmBookingAfterPayment(bookingId);
    } else if (bookingId && joinSession === 'true') {
      // Joining an existing session
      loadBookingAndJoin(bookingId);
    } else if (bookingId && paymentStatus === 'cancelled') {
      toast.info('Payment was cancelled. You can try again.');
    }
  }, []);
  
  const confirmBookingAfterPayment = async (bookingId) => {
    try {
      // Confirm the booking on backend
      const confirmRes = await fetch(`${API}/api/live-studio/bookings/${bookingId}/confirm`, {
        method: 'POST'
      });
      
      if (confirmRes.ok) {
        // Load booking details
        const res = await fetch(`${API}/api/live-studio/bookings/${bookingId}`);
        if (res.ok) {
          const data = await res.json();
          setBooking(data.booking);
          
          // Clear URL params
          window.history.replaceState({}, '', window.location.pathname);
          
          // IMMEDIATELY go to video room - no waiting for email
          toast.success('Payment successful! Entering studio...');
          setStep(5);
          
          // Small delay to ensure state is set before initializing video
          setTimeout(() => {
            initializeVideoRoom();
          }, 100);
        }
      } else {
        toast.error('Could not confirm booking. Please contact support.');
      }
    } catch (err) {
      console.error('Error confirming booking:', err);
      toast.error('Error confirming your booking.');
    }
  };
  
  const loadBookingAndJoin = async (bookingId) => {
    try {
      const res = await fetch(`${API}/api/live-studio/bookings/${bookingId}`);
      if (res.ok) {
        const data = await res.json();
        setBooking(data.booking);
        setStep(5);
        initializeVideoRoom();
      }
    } catch (err) {
      console.error('Error loading booking:', err);
    }
  };
  
  const handleBookSession = async () => {
    if (!customerName || !customerEmail || !selectedSessionType) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/api/live-studio/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerName,
          customer_email: customerEmail,
          date: selectedDate.toISOString().split('T')[0],
          time: selectedTime,
          duration: selectedSessionType.duration,
          session_type: selectedSessionType.id,
          price: selectedSessionType.price,
          notes: sessionNotes
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Booking failed');
      }
      
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const initializeVideoRoom = async () => {
    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      // Initialize WebRTC peer connection
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' }
        ]
      });
      
      peerConnectionRef.current = pc;
      localStreamRef.current = stream;
      
      // Add local tracks to peer connection
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });
      
      // Handle incoming tracks (remote video)
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
        toast.success('Mestre connected!');
      };
      
      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'ice-candidate',
            candidate: event.candidate
          }));
        }
      };
      
      // Connect to signaling server
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.REACT_APP_BACKEND_URL?.replace('https://', '').replace('http://', '') || window.location.host;
      const roomId = booking?.id || booking?.room_id || 'test-room';
      console.log('Client connecting to room:', roomId);
      const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/live-studio/ws/${roomId}/client`);
      
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setInSession(true);
        toast.success('Connected to studio!');
      };
      
      ws.onmessage = async (event) => {
        const message = JSON.parse(event.data);
        
        if (message.type === 'peer_joined') {
          // Host joined, create and send offer
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          ws.send(JSON.stringify({
            type: 'offer',
            sdp: offer.sdp
          }));
        }
        
        if (message.type === 'answer') {
          // Received answer from host
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'answer',
            sdp: message.sdp
          }));
        }
        
        if (message.type === 'offer') {
          // Received offer from host (if host initiates)
          await pc.setRemoteDescription(new RTCSessionDescription({
            type: 'offer',
            sdp: message.sdp
          }));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          ws.send(JSON.stringify({
            type: 'answer',
            sdp: answer.sdp
          }));
        }
        
        if (message.type === 'ice-candidate' && message.candidate) {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(message.candidate));
          } catch (e) {
            console.error('Error adding ICE candidate:', e);
          }
        }
        
        if (message.type === 'peer_left') {
          toast.info('Mestre disconnected');
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('WebSocket closed');
      };
      
    } catch (err) {
      console.error('Media error:', err);
      toast.error('Could not access camera/microphone. Please check permissions.');
    }
  };
  
  const toggleMic = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setMicEnabled(audioTrack.enabled);
      }
    }
  };
  
  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
      }
    }
  };
  
  const endSession = () => {
    // Stop local media tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // Clear video elements
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    
    setInSession(false);
    toast.info('Session ended');
  };
  
  const totalPrice = selectedSessionType?.price || (duration * HOURLY_RATE);

  // Generate a short, human-friendly room code (matches SimpleVideoRoom style: 6-char uppercase)
  const generateRoomCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

  // ===== Admin invite handlers =====
  const handleCreateInvite = async () => {
    setInviteLoading(true);
    setInviteEmailSent(false);
    try {
      // Just generate a code — SimpleVideoRoom uses room code directly, no backend record needed
      const code = generateRoomCode();
      const joinLink = `${window.location.origin}/video-room/${code}`;
      setInviteSession({ code, join_link: joinLink });
      toast.success(`Room ${code} ready — copy the link and share it`);
    } catch (err) {
      toast.error(err.message || 'Could not create invite');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteSession?.join_link) return;
    try {
      await navigator.clipboard.writeText(inviteSession.join_link);
      toast.success('Invite link copied to clipboard');
    } catch {
      toast.info(inviteSession.join_link);
    }
  };

  const handleCopyInviteCode = async () => {
    if (!inviteSession?.code) return;
    try {
      await navigator.clipboard.writeText(inviteSession.code);
      toast.success('Code copied');
    } catch {
      toast.info(inviteSession.code);
    }
  };

  const handleSendInviteEmail = async () => {
    // Email invite intentionally simplified — SimpleVideoRoom rooms are ephemeral so
    // there's no backend record to attach to. If needed later, we can add a route
    // that just emails the plain link. For now, guide the user to copy + paste.
    if (!inviteEmail || !inviteSession?.join_link) {
      toast.error('Enter an email and create a code first');
      return;
    }
    // Copy the link and prompt the user to paste — no backend dependency
    try {
      await navigator.clipboard.writeText(inviteSession.join_link);
      toast.success(`Link copied — paste it in your email to ${inviteEmail}`);
      setInviteEmailSent(true);
    } catch {
      toast.info(inviteSession.join_link);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <AdminFreeAccessBar
        title="Admin — Live Studio Free Access"
        subtitle="Skip booking. Spin up a live video room with a code you can share."
        testid="admin-bar-live-studio"
        primary={{
          label: 'Start Live Room',
          icon: Video,
          onClick: () => {
            const code = generateStudioRoomCode();
            window.location.href = `/video-room/${code}/host`;
          },
        }}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <PageEditable path="hero.title" as="span" fallback="Live Studio Session" />
          </h1>
          <p className="text-xl text-green-100">
            <PageEditable path="hero.subtitle" as="span" fallback="Book a live recording session with Mestre Curtis Pierre" />
          </p>
          <p className="text-green-200 mt-2">
            <PageEditable path="hero.tagline" as="span" fallback={`Record together in real-time via video • $${HOURLY_RATE}/hour`} />
          </p>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Admin trial-mode bypass — visible only when logged in as admin */}
        {isAdminUser && step < 5 && (
          <div className="mb-8 p-5 rounded-2xl border-2 border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-amber-500/10">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <Crown className="w-6 h-6 text-yellow-400 shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-yellow-300 text-lg">
                    <PageEditable path="adminTrial.title" as="span" fallback="Admin Trial Mode" />
                  </h3>
                  <p className="text-sm text-yellow-100/80 mt-1 max-w-xl">
                    <PageEditable path="adminTrial.description" as="span" fallback="Skip booking + payment and jump straight into the Live Studio room to verify audio, video, mic, and camera work end-to-end. No charge, no session cap." />
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  // Generate a code and route directly to the working WebRTC room as HOST.
                  // SimpleVideoRoom (/video-room/:code/host) is the Zoom-like room with the
                  // big code display, copy-link button, and functional peer connection.
                  const code = generateRoomCode();
                  toast.success(`Room ${code} — entering as host`);
                  window.location.href = `/video-room/${code}/host`;
                }}
                data-testid="admin-trial-enter-studio-btn"
                className="px-6 py-3 rounded-xl font-bold bg-yellow-500 hover:bg-yellow-400 text-black transition-colors flex items-center gap-2 shrink-0"
              >
                <Video className="w-5 h-5" /> <PageEditable path="adminTrial.cta" as="span" fallback="Enter Studio Now (Free)" />
              </button>
            </div>
          </div>
        )}

        {/* Admin Invite Panel — create a shareable code/link for a guest */}
        {isAdminUser && step < 5 && (
          <div
            className="mb-8 p-5 rounded-2xl border-2 border-green-500/50 bg-gradient-to-r from-green-500/10 to-emerald-500/10"
            data-testid="admin-invite-panel"
          >
            <div className="flex items-start gap-3 mb-4">
              <Users className="w-6 h-6 text-green-400 shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-green-300 text-lg">
                  <PageEditable path="invite.title" as="span" fallback="Invite Someone to Your Live Session" />
                </h3>
                <p className="text-sm text-green-100/80 mt-1 max-w-2xl">
                  <PageEditable path="invite.description" as="span" fallback="Generate a one-time join code + shareable link. Send it to your student/client — they open the link and land in your live room." />
                </p>
              </div>
            </div>

            {!inviteSession ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Optional: guest email (for email invite)"
                  className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-green-500 focus:outline-none"
                  data-testid="admin-invite-email-input"
                />
                <button
                  type="button"
                  onClick={handleCreateInvite}
                  disabled={inviteLoading}
                  data-testid="admin-create-invite-btn"
                  className="px-6 py-3 rounded-xl font-bold bg-green-500 hover:bg-green-400 text-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {inviteLoading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>
                      <LinkIcon className="w-5 h-5" /> Create Invite Link
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Code + Copy */}
                <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                  <div className="flex-1 bg-[#0a0a0a] border border-green-500/30 rounded-lg px-4 py-3 flex items-center gap-3">
                    <span className="text-xs uppercase tracking-wider text-green-400/70">Code</span>
                    <span
                      className="font-mono text-lg font-bold text-white select-all"
                      data-testid="admin-invite-code"
                    >
                      {inviteSession.code}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyInviteCode}
                    data-testid="admin-copy-code-btn"
                    className="px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white flex items-center justify-center gap-2"
                  >
                    <Copy className="w-4 h-4" /> Copy Code
                  </button>
                </div>

                {/* Shareable Link + Copy */}
                <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                  <div className="flex-1 bg-[#0a0a0a] border border-green-500/30 rounded-lg px-4 py-3 flex items-center gap-3 overflow-hidden">
                    <LinkIcon className="w-4 h-4 text-green-400 shrink-0" />
                    <span
                      className="text-sm text-white truncate select-all"
                      data-testid="admin-invite-link"
                    >
                      {inviteSession.join_link}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyInviteLink}
                    data-testid="admin-copy-link-btn"
                    className="px-4 py-3 rounded-lg bg-green-500 hover:bg-green-400 text-black font-bold flex items-center justify-center gap-2"
                  >
                    <Copy className="w-4 h-4" /> Copy Link
                  </button>
                </div>

                {/* Email invite (optional) */}
                <div className="flex flex-col sm:flex-row gap-2 items-stretch">
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="guest@email.com"
                    className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-green-500 focus:outline-none"
                    data-testid="admin-invite-email-input-2"
                  />
                  <button
                    type="button"
                    onClick={handleSendInviteEmail}
                    disabled={inviteLoading || !inviteEmail}
                    data-testid="admin-send-invite-email-btn"
                    className="px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Mail className="w-4 h-4" />
                    {inviteEmailSent ? 'Sent ✓' : 'Email Invite'}
                  </button>
                </div>

                {/* Enter host room + reset */}
                <div className="flex flex-col sm:flex-row gap-2 items-stretch pt-2">
                  <a
                    href={`/video-room/${inviteSession.code}/host`}
                    data-testid="admin-open-host-room-btn"
                    className="flex-1 px-6 py-3 rounded-xl font-bold bg-emerald-500 hover:bg-emerald-400 text-black transition-colors flex items-center justify-center gap-2"
                  >
                    <Video className="w-5 h-5" /> Enter as Host
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <a
                    href={`/video-room/${inviteSession.code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="admin-open-guest-preview-btn"
                    className="flex-1 px-6 py-3 rounded-xl font-bold bg-blue-500 hover:bg-blue-400 text-black transition-colors flex items-center justify-center gap-2"
                  >
                    <Users className="w-5 h-5" /> Preview as Guest
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => { setInviteSession(null); setInviteEmailSent(false); }}
                    data-testid="admin-reset-invite-btn"
                    className="px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-white text-sm"
                  >
                    Reset
                  </button>
                </div>

                <p className="text-xs text-green-200/70 mt-1">
                  <PageEditable path="invite.footerNote" as="span" fallback="Send the code or link to your guest. They open it, land at the join page, and connect to your room." />
                </p>
              </div>
            )}
          </div>
        )}

        {/* Join by Code — for anyone (host reopening a session, guest with code) */}
        {step < 5 && (
          <div
            className="mb-8 p-5 rounded-2xl border-2 border-blue-500/40 bg-gradient-to-r from-blue-500/10 to-cyan-500/10"
            data-testid="join-by-code-panel"
          >
            <div className="flex items-start gap-3 mb-3">
              <Video className="w-6 h-6 text-blue-400 shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-blue-300 text-lg">
                  <PageEditable path="joinCode.title" as="span" fallback="Already have a code? Join here" />
                </h3>
                <p className="text-sm text-blue-100/80 mt-1">
                  <PageEditable path="joinCode.description" as="span" fallback="Paste an 8-character session code to jump straight into the room." />
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={joinCodeInput}
                onChange={(e) => setJoinCodeInput(e.target.value.trim().toUpperCase())}
                placeholder="e.g. KS1NJIMI"
                maxLength={16}
                className="flex-1 bg-[#0a0a0a] border border-gray-700 rounded-lg px-4 py-3 text-white font-mono focus:border-blue-500 focus:outline-none"
                data-testid="join-code-input"
              />
              {isAdminUser && (
                <button
                  type="button"
                  onClick={() => {
                    if (!joinCodeInput) return toast.error('Enter a code first');
                    window.location.href = `/video-room/${joinCodeInput}/host`;
                  }}
                  data-testid="join-as-host-btn"
                  className="px-5 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold flex items-center justify-center gap-2"
                >
                  <Video className="w-4 h-4" /> Join as Host
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!joinCodeInput) return toast.error('Enter a code first');
                  window.location.href = `/video-room/${joinCodeInput}`;
                }}
                data-testid="join-as-guest-btn"
                className="px-5 py-3 rounded-lg bg-blue-500 hover:bg-blue-400 text-black font-bold flex items-center justify-center gap-2"
              >
                <Users className="w-4 h-4" /> Join as Guest
              </button>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        {step < 5 && (
          <div className="flex items-center justify-center mb-12">
            {[
              { num: 1, labelPath: 'steps.1', label: 'Select Time' },
              { num: 2, labelPath: 'steps.2', label: 'Your Details' },
              { num: 3, labelPath: 'steps.3', label: 'Payment' },
              { num: 4, labelPath: 'steps.4', label: 'Confirmed' }
            ].map((s, idx) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-green-500' : 'text-gray-500'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= s.num ? 'bg-green-500 text-black' : 'bg-gray-800 text-gray-500'
                  }`}>
                    {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                  </div>
                  <span className="hidden md:inline text-sm">
                    <PageEditable path={s.labelPath} as="span" fallback={s.label} />
                  </span>
                </div>
                {idx < 3 && <div className={`w-12 md:w-20 h-0.5 mx-2 ${step > s.num ? 'bg-green-500' : 'bg-gray-800'}`} />}
              </React.Fragment>
            ))}
          </div>
        )}
        
        <AnimatePresence mode="wait">
          {/* Step 1: Calendar & Time Selection */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-[#141414] rounded-2xl p-6 md:p-8"
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Calendar className="w-6 h-6 text-green-500" />
                <PageEditable path="step1.title" as="span" fallback="Select Date & Time" />
              </h2>
              
              {/* Start Now Button */}
              <div className="mb-6 p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-green-400 flex items-center gap-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <PageEditable path="startNow.title" as="span" fallback="Start a Session Now" />
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      <PageEditable path="startNow.description" as="span" fallback="Skip scheduling - begin immediately" />
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const now = new Date();
                      setSelectedDate(now);
                      const hours = now.getHours();
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      const hour12 = hours % 12 || 12;
                      setSelectedTime(`${hour12.toString().padStart(2, '0')}:00 ${ampm}`);
                      setStep(2);
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-xl"
                  >
                    <PageEditable path="startNow.cta" as="span" fallback="Start Now" />
                  </button>
                </div>
              </div>
              
              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#141414] text-gray-500">
                    <PageEditable path="step1.scheduleLater" as="span" fallback="or schedule for later" />
                  </span>
                </div>
              </div>
              
              {/* Calendar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                    className="p-2 hover:bg-gray-800 rounded-lg"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="text-lg font-semibold">
                    {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </h3>
                  <button 
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                    className="p-2 hover:bg-gray-800 rounded-lg"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="grid grid-cols-7 gap-1 text-center text-sm">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="py-2 text-gray-500 font-medium">{day}</div>
                  ))}
                  {getDaysInMonth(currentMonth).map((date, idx) => (
                    <button
                      key={idx}
                      disabled={!isDateAvailable(date)}
                      onClick={() => date && setSelectedDate(date)}
                      className={`py-3 rounded-lg transition-all ${
                        !date ? 'invisible' :
                        !isDateAvailable(date) ? 'text-gray-700 cursor-not-allowed' :
                        selectedDate?.toDateString() === date.toDateString() 
                          ? 'bg-green-500 text-black font-bold' 
                          : 'hover:bg-gray-800 text-white'
                      }`}
                    >
                      {date?.getDate()}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Time Slots */}
              {selectedDate && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-green-500" />
                    <PageEditable path="step1.availableTimesPrefix" as="span" fallback="Available Times for" /> {selectedDate.toLocaleDateString()}
                  </h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {TIME_SLOTS.map(time => (
                      <button
                        key={time}
                        disabled={isTimeSlotBooked(time)}
                        onClick={() => setSelectedTime(time)}
                        className={`py-3 px-4 rounded-lg text-sm font-medium transition-all ${
                          isTimeSlotBooked(time) 
                            ? 'bg-gray-800 text-gray-600 cursor-not-allowed line-through' 
                            : selectedTime === time
                              ? 'bg-green-500 text-black'
                              : 'bg-gray-800 hover:bg-gray-700'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Duration */}
              {selectedTime && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold mb-4">
                    <PageEditable path="sessionType.heading" as="span" fallback="Session Type" />
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {SESSION_TYPES.map(session => (
                      <button
                        key={session.id}
                        onClick={() => {
                          setSelectedSessionType(session);
                          setDuration(session.duration);
                        }}
                        className={`p-4 rounded-xl border-2 transition-all text-left ${
                          selectedSessionType?.id === session.id
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-gray-700 hover:border-gray-600'
                        } ${session.id === 'test' ? 'border-amber-500/50' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-bold">
                            <PageEditable path={`sessionTypes.${session.id}.name`} as="span" fallback={session.name} />
                          </div>
                          {session.id === 'test' && (
                            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">TEST</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-400 mb-2">
                          <PageEditable path={`sessionTypes.${session.id}.description`} as="span" fallback={session.description} />
                        </div>
                        <div className="text-2xl font-bold text-green-500">$<PageEditable path={`sessionTypes.${session.id}.price`} as="span" fallback={String(session.price)} /></div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Continue Button */}
              <button
                disabled={!selectedDate || !selectedTime || !selectedSessionType}
                onClick={() => setStep(2)}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-black font-bold rounded-xl transition-all"
              >
                <PageEditable path="step1.continueCta" as="span" fallback="Continue" />
              </button>
            </motion.div>
          )}
          
          {/* Step 2: Customer Details */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-[#141414] rounded-2xl p-6 md:p-8"
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Users className="w-6 h-6 text-green-500" />
                <PageEditable path="step2.title" as="span" fallback="Your Details" />
              </h2>
              
              {/* Session Summary */}
              <div className="bg-[#0a0a0a] rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <p className="text-gray-400 text-sm">Session Date</p>
                    <p className="font-semibold">{selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-sm">Time</p>
                    <p className="font-semibold">{selectedTime}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-800">
                  <div>
                    <p className="text-gray-400 text-sm">Session Type</p>
                    <p className="font-semibold">{selectedSessionType?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400 text-sm">Price</p>
                    <p className="font-semibold text-green-500">${selectedSessionType?.price}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <PageEditable path="step2.nameLabel" as="span" fallback="Your Name *" />
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <PageEditable path="step2.emailLabel" as="span" fallback="Email *" />
                  </label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <PageEditable path="step2.notesLabel" as="span" fallback="Session Notes (optional)" />
                  </label>
                  <textarea
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    placeholder="What would you like to work on? Any specific instruments or styles?"
                    rows={3}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-gray-800 rounded-xl focus:border-green-500 focus:outline-none resize-none"
                  />
                </div>
              </div>
              
              <div className="flex gap-4 mt-8">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-4 bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold"
                >
                  <PageEditable path="common.back" as="span" fallback="Back" />
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!customerName || !customerEmail}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 disabled:from-gray-700 disabled:to-gray-700 text-black font-bold rounded-xl"
                >
                  <PageEditable path="step2.continueCta" as="span" fallback="Continue to Payment" />
                </button>
              </div>
            </motion.div>
          )}
          
          {/* Step 3: Payment */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-[#141414] rounded-2xl p-6 md:p-8"
            >
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <DollarSign className="w-6 h-6 text-green-500" />
                <PageEditable path="step3.title" as="span" fallback="Confirm & Pay" />
              </h2>
              
              {/* Booking Summary */}
              <div className="bg-[#0a0a0a] rounded-xl p-6 mb-6">
                <h3 className="font-semibold mb-4 text-lg">
                  <PageEditable path="step3.summaryTitle" as="span" fallback="Booking Summary" />
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Date</span>
                    <span>{selectedDate?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Time</span>
                    <span>{selectedTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Duration</span>
                    <span>{duration} hour{duration > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Rate</span>
                    <span>${HOURLY_RATE}/hour</span>
                  </div>
                  <div className="border-t border-gray-800 my-3"></div>
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-green-500">${totalPrice}</span>
                  </div>
                </div>
              </div>
              
              {/* What's Included */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-green-400 mb-2">
                  <PageEditable path="step3.includedTitle" as="span" fallback="What's Included:" />
                </h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• <PageEditable path="step3.included.0" as="span" fallback="Live video session with Mestre Curtis Pierre" /></li>
                  <li>• <PageEditable path="step3.included.1" as="span" fallback="Real-time recording collaboration" /></li>
                  <li>• <PageEditable path="step3.included.2" as="span" fallback="Professional audio capture" /></li>
                  <li>• <PageEditable path="step3.included.3" as="span" fallback="Session recording delivered after" /></li>
                </ul>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-4 bg-gray-800 hover:bg-gray-700 rounded-xl font-semibold"
                >
                  <PageEditable path="common.back" as="span" fallback="Back" />
                </button>
                <button
                  onClick={handleBookSession}
                  disabled={loading}
                  className="flex-1 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-xl flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>
                      <DollarSign className="w-5 h-5" />
                      <PageEditable path="step3.payCtaPrefix" as="span" fallback="Pay" /> ${totalPrice}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          )}
          
          {/* Step 4: Confirmation */}
          {step === 4 && booking && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#141414] rounded-2xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              
              <h2 className="text-3xl font-bold mb-4">
                <PageEditable path="step4.title" as="span" fallback="Session Booked!" />
              </h2>
              <p className="text-gray-400 mb-6">
                <PageEditable path="step4.description" as="span" fallback="Your live studio session is confirmed. You'll receive an email with the session link." />
              </p>
              
              <div className="bg-[#0a0a0a] rounded-xl p-6 mb-6 text-left max-w-md mx-auto">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Date</span>
                    <span className="font-semibold">{booking.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Time</span>
                    <span className="font-semibold">{booking.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Duration</span>
                    <span className="font-semibold">{booking.duration} hour(s)</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                <p className="text-amber-400 text-sm">
                  📧 <PageEditable path="step4.emailNotePrefix" as="span" fallback="A confirmation email has been sent to" /> <strong>{customerEmail}</strong>
                </p>
              </div>
              
              <button
                onClick={() => {
                  setStep(5);
                  initializeVideoRoom();
                }}
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 text-black font-bold rounded-xl inline-flex items-center gap-2"
              >
                <Video className="w-5 h-5" />
                <PageEditable path="step4.joinCta" as="span" fallback="Join Studio Now" />
              </button>
            </motion.div>
          )}
          
          {/* Step 5: Live Video Room */}
          {step === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[#141414] rounded-2xl overflow-hidden"
            >
              {/* Video Area */}
              <div className="relative aspect-video bg-black">
                {/* Remote Video (Mestre) - Main View */}
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                
                {/* Placeholder when no remote video */}
                {!remoteVideoRef.current?.srcObject && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mb-4">
                      <Music className="w-16 h-16 text-black" />
                    </div>
                    <h3 className="text-xl font-bold">
                      <PageEditable path="videoRoom.mentorName" as="span" fallback="Mestre Curtis Pierre" />
                    </h3>
                    <p className="text-gray-400">
                      <PageEditable path="videoRoom.waitingLabel" as="span" fallback="Waiting to connect..." />
                    </p>
                  </div>
                )}
                
                {/* Local Video (Customer) - Picture in Picture */}
                <div className="absolute bottom-4 right-4 w-48 aspect-video rounded-xl overflow-hidden border-2 border-green-500 shadow-2xl">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {!videoEnabled && (
                    <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                      <VideoOff className="w-8 h-8 text-gray-500" />
                    </div>
                  )}
                </div>
                
                {/* Session Info Overlay */}
                <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-3">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">
                    <PageEditable path="videoRoom.liveBadge" as="span" fallback="LIVE SESSION" />
                  </span>
                </div>
              </div>
              
              {/* Controls */}
              <div className="p-6 bg-[#0a0a0a]">
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={toggleMic}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                      micEnabled ? 'bg-gray-800 hover:bg-gray-700' : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {micEnabled ? <Mic className="w-6 h-6" /> : <MicOff className="w-6 h-6" />}
                  </button>
                  
                  <button
                    onClick={toggleVideo}
                    className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                      videoEnabled ? 'bg-gray-800 hover:bg-gray-700' : 'bg-red-500 hover:bg-red-600'
                    }`}
                  >
                    {videoEnabled ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                  </button>
                  
                  <button
                    onClick={endSession}
                    className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
                  >
                    <PhoneOff className="w-6 h-6" />
                  </button>
                  
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="w-14 h-14 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center"
                  >
                    {isFullscreen ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
                  </button>
                </div>
                
                <p className="text-center text-gray-500 text-sm mt-4">
                  {inSession ? (
                    <PageEditable path="videoRoom.connectedLabel" as="span" fallback="Connected • Recording available after session" />
                  ) : (
                    <PageEditable path="videoRoom.connectingLabel" as="span" fallback="Connecting..." />
                  )}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const LiveStudioBooking = () => (
  <PageEditProvider slug="live-studio">
    <LiveStudioBookingInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export default LiveStudioBooking;
